import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Users, TrendingUp, DollarSign, MessageSquare, Calendar,
  Target, Briefcase, Eye, Building2, Loader2, ArrowUpDown, ChevronDown, ChevronUp
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend
} from "recharts";

const AgentPerformanceView = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("performance_score");
  const [expandedAgent, setExpandedAgent] = useState(null);

  useEffect(() => { fetchAgents(); }, []);

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/analytics/agents-performance`, { withCredentials: true });
      setAgents(res.data.agents || []);
    } catch (error) {
      console.error("Error fetching agent performance:", error);
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...agents].sort((a, b) => {
    if (sortBy === "name") return a.name.localeCompare(b.name);
    return (b[sortBy] || 0) - (a[sortBy] || 0);
  });

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  // Build comparison chart data (top 10)
  const chartData = sorted.slice(0, 10).map((a) => ({
    name: a.name.split(" ")[0],
    Revenue: a.revenue,
    Deals: a.deals_won,
    Leads: a.leads_won,
    Tours: a.tours_completed,
  }));

  return (
    <div className="space-y-6" data-testid="agent-performance-view">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-serif">Agent Performance</h2>
          <p className="text-muted-foreground text-sm">Individual performance metrics for every agent ({agents.length} agents)</p>
        </div>
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 h-9" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="performance_score">Performance Score</SelectItem>
              <SelectItem value="revenue">Revenue</SelectItem>
              <SelectItem value="deals_won">Deals Won</SelectItem>
              <SelectItem value="reply_rate">Reply Rate</SelectItem>
              <SelectItem value="tour_completion_rate">Tour Completion</SelectItem>
              <SelectItem value="lead_conversion_rate">Lead Conversion</SelectItem>
              <SelectItem value="properties">Properties</SelectItem>
              <SelectItem value="name">Name (A-Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Comparison Chart */}
      {chartData.length > 0 && (
        <div className="bg-card border rounded-sm p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" /> Agent Comparison
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Deals" fill="#38a169" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Leads" fill="#3182ce" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Tours" fill="#d69e2e" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Agent Cards */}
      <div className="space-y-3">
        {sorted.map((agent, rank) => (
          <div key={agent.agent_id} className="bg-card border rounded-sm overflow-hidden" data-testid={`agent-card-${agent.agent_id}`}>
            {/* Summary Row */}
            <button
              onClick={() => setExpandedAgent(expandedAgent === agent.agent_id ? null : agent.agent_id)}
              className="w-full flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors text-left"
              data-testid={`expand-agent-${agent.agent_id}`}
            >
              <span className="text-lg font-bold text-muted-foreground w-8">#{rank + 1}</span>
              <img
                src={agent.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name)}&size=40`}
                alt={agent.name}
                className="w-10 h-10 rounded-full shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{agent.name}</span>
                  <Badge variant="outline" className="text-[10px] py-0 capitalize">{agent.role}</Badge>
                </div>
                <span className="text-xs text-muted-foreground">{agent.email}</span>
              </div>

              {/* Quick KPIs */}
              <div className="hidden sm:flex items-center gap-6 text-sm">
                <KpiPill icon={<DollarSign className="w-3.5 h-3.5" />} label="Revenue" value={`K${agent.revenue.toLocaleString()}`} />
                <KpiPill icon={<Briefcase className="w-3.5 h-3.5" />} label="Deals Won" value={agent.deals_won} />
                <KpiPill icon={<MessageSquare className="w-3.5 h-3.5" />} label="Reply Rate" value={`${agent.reply_rate}%`} />
                <KpiPill icon={<Target className="w-3.5 h-3.5" />} label="Score" value={agent.performance_score} highlight />
              </div>

              {expandedAgent === agent.agent_id
                ? <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" />
                : <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
              }
            </button>

            {/* Expanded Detail */}
            {expandedAgent === agent.agent_id && (
              <div className="border-t bg-muted/20 p-4 space-y-4">
                {/* Detailed Metrics Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                  <MetricCard icon={<Building2 className="w-4 h-4" />} label="Properties" value={agent.properties} sub={`${agent.active_properties} active`} />
                  <MetricCard icon={<Eye className="w-4 h-4" />} label="Total Views" value={agent.total_views.toLocaleString()} />
                  <MetricCard icon={<Briefcase className="w-4 h-4" />} label="Total Deals" value={agent.total_deals} sub={`${agent.deals_won} won / ${agent.deals_lost} lost`} />
                  <MetricCard icon={<DollarSign className="w-4 h-4" />} label="Revenue" value={`K${agent.revenue.toLocaleString()}`} />
                  <MetricCard icon={<Target className="w-4 h-4" />} label="Win Rate" value={`${agent.win_rate}%`} bar={agent.win_rate} barColor="bg-emerald-500" />
                  <MetricCard icon={<MessageSquare className="w-4 h-4" />} label="Inquiries" value={agent.total_inquiries} sub={`${agent.inquiries_replied} replied`} />
                  <MetricCard icon={<MessageSquare className="w-4 h-4" />} label="Reply Rate" value={`${agent.reply_rate}%`} bar={agent.reply_rate} barColor="bg-blue-500" />
                  <MetricCard icon={<Calendar className="w-4 h-4" />} label="Tours" value={agent.total_tours} sub={`${agent.tours_completed} completed`} />
                  <MetricCard icon={<Calendar className="w-4 h-4" />} label="Tour Completion" value={`${agent.tour_completion_rate}%`} bar={agent.tour_completion_rate} barColor="bg-teal-500" />
                  <MetricCard icon={<Users className="w-4 h-4" />} label="Leads" value={agent.total_leads} sub={`${agent.leads_won} converted`} />
                  <MetricCard icon={<Users className="w-4 h-4" />} label="Lead Conversion" value={`${agent.lead_conversion_rate}%`} bar={agent.lead_conversion_rate} barColor="bg-purple-500" />
                  <MetricCard icon={<TrendingUp className="w-4 h-4" />} label="Performance Score" value={agent.performance_score} highlight />
                </div>

                {/* Radar Chart */}
                <div className="bg-card border rounded-sm p-4">
                  <h4 className="text-sm font-medium mb-2">Performance Profile</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <RadarChart data={[
                      { metric: "Win Rate", value: agent.win_rate },
                      { metric: "Reply Rate", value: agent.reply_rate },
                      { metric: "Tour Comp.", value: agent.tour_completion_rate },
                      { metric: "Lead Conv.", value: agent.lead_conversion_rate },
                      { metric: "Activity", value: Math.min((agent.total_deals + agent.total_inquiries + agent.total_tours) * 5, 100) },
                    ]}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9 }} />
                      <Radar dataKey="value" stroke="#1a365d" fill="#1a365d" fillOpacity={0.2} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        ))}

        {agents.length === 0 && (
          <div className="text-center py-16 bg-muted rounded-sm">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No agents found</p>
          </div>
        )}
      </div>
    </div>
  );
};

function KpiPill({ icon, label, value, highlight }) {
  return (
    <div className={`flex items-center gap-1.5 ${highlight ? "text-primary font-semibold" : "text-muted-foreground"}`}>
      {icon}
      <span className="font-medium">{value}</span>
    </div>
  );
}

function MetricCard({ icon, label, value, sub, bar, barColor, highlight }) {
  return (
    <div className={`bg-card border rounded-sm p-3 ${highlight ? "border-primary/30 bg-primary/5" : ""}`}>
      <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
        {icon}
        <span className="text-[11px]">{label}</span>
      </div>
      <div className={`text-lg font-bold ${highlight ? "text-primary" : ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
      {bar !== undefined && (
        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
          <div className={`${barColor} h-1.5 rounded-full transition-all`} style={{ width: `${Math.min(bar, 100)}%` }} />
        </div>
      )}
    </div>
  );
}

export default AgentPerformanceView;
